/**
 * Sample library — browse all curated resume/JD pairs with preview + quick-load.
 *
 * Each card shows the sample metadata, tier badge, and truncated previews
 * of both the resume and JD. Click "Try this sample" to jump to the demo
 * page with it preloaded.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Briefcase,
} from "lucide-react";

import { SAMPLES, type Sample, type SampleCategory } from "@/samples";

const CATEGORY_STYLE: Record<
  SampleCategory,
  { bg: string; text: string; dot: string; label: string }
> = {
  "direct-match": {
    bg: "bg-emerald-500/10 border-emerald-500/30",
    text: "text-emerald-300",
    dot: "bg-emerald-400",
    label: "Direct match",
  },
  "partial-overlap": {
    bg: "bg-amber-500/10 border-amber-500/30",
    text: "text-amber-300",
    dot: "bg-amber-400",
    label: "Partial overlap",
  },
  "cross-domain": {
    bg: "bg-sky-500/10 border-sky-500/30",
    text: "text-sky-300",
    dot: "bg-sky-400",
    label: "Cross-domain",
  },
  mismatch: {
    bg: "bg-rose-500/10 border-rose-500/30",
    text: "text-rose-300",
    dot: "bg-rose-400",
    label: "Mismatch",
  },
};

export default function Samples() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-24">
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-100 mb-4">
          Sample Library
        </h1>
        <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Ten hand-curated resume/JD pairs across software, ML, healthcare, finance,
          marketing, legal, and operations — covering strong fits, partial matches,
          cross-domain transfers, and deliberate mismatches. All people are fictional.
          Category badges describe the relationship between the resume and JD — run the
          match to see how the engine scores it.
        </p>
      </div>

      {/* Category legend */}
      <div className="flex flex-wrap gap-3 justify-center mb-8">
        {(["direct-match", "partial-overlap", "cross-domain", "mismatch"] as SampleCategory[]).map((cat) => {
          const s = CATEGORY_STYLE[cat];
          return (
            <span
              key={cat}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border ${s.bg} ${s.text}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              {s.label}
            </span>
          );
        })}
      </div>

      {/* Sample cards */}
      <div className="space-y-4">
        {SAMPLES.map((sample) => (
          <SampleCard
            key={sample.id}
            sample={sample}
            expanded={expandedId === sample.id}
            onToggle={() =>
              setExpandedId(expandedId === sample.id ? null : sample.id)
            }
          />
        ))}
      </div>

      {/* Benchmark note */}
      <div className="mt-12 ta-card p-6 text-center">
        <h2 className="text-base font-semibold text-slate-100 mb-2">
          Beyond samples: validated benchmark
        </h2>
        <p className="text-sm text-slate-400 max-w-xl mx-auto">
          The scoring engine is also regression-tested against an 8-query,
          32-candidate labeled benchmark dataset in CI. Every code change is
          measured — the build fails if nDCG@5 drops below the floor.
        </p>
      </div>
    </section>
  );
}

function SampleCard({
  sample,
  expanded,
  onToggle,
}: {
  sample: Sample;
  expanded: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const tier = CATEGORY_STYLE[sample.category];

  function tryInDemo() {
    // Store the sample in sessionStorage so Home can pick it up
    sessionStorage.setItem("ta-preload-sample", sample.id);
    navigate("/demo");
  }

  return (
    <div className="ta-card overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-start gap-4 hover:bg-slate-900/30 transition"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-slate-100">
              {sample.label}
            </h3>
            <span
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${tier.bg} ${tier.text}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${tier.dot}`} />
              {tier.label}
            </span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            {sample.summary}
          </p>
        </div>
        <div className="text-slate-500 shrink-0 mt-1">
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-slate-800/60 px-5 py-4 bg-slate-950/30">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Resume preview */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <FileText className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  Resume
                </span>
                <span className="text-[10px] text-slate-600">
                  {sample.resumeText.length.toLocaleString()} chars
                </span>
              </div>
              <pre className="text-xs text-slate-400 font-mono bg-slate-900/50 border border-slate-800/60 rounded-lg p-3 max-h-[200px] overflow-y-auto whitespace-pre-wrap leading-relaxed">
                {sample.resumeText.slice(0, 600)}
                {sample.resumeText.length > 600 && (
                  <span className="text-slate-600">
                    {"\n\n"}... ({sample.resumeText.length - 600} more chars)
                  </span>
                )}
              </pre>
            </div>

            {/* JD preview */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Briefcase className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  Job Description
                </span>
                <span className="text-[10px] text-slate-600">
                  {sample.jdText.length.toLocaleString()} chars
                </span>
              </div>
              <pre className="text-xs text-slate-400 font-mono bg-slate-900/50 border border-slate-800/60 rounded-lg p-3 max-h-[200px] overflow-y-auto whitespace-pre-wrap leading-relaxed">
                {sample.jdText.slice(0, 600)}
                {sample.jdText.length > 600 && (
                  <span className="text-slate-600">
                    {"\n\n"}... ({sample.jdText.length - 600} more chars)
                  </span>
                )}
              </pre>
            </div>
          </div>

          {/* Why this sample */}
          <WhyThisSample sample={sample} />

          {/* Action */}
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={tryInDemo}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all flex items-center gap-2"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Try this sample
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WhyThisSample({ sample }: { sample: Sample }) {
  const explanations: Record<string, { what: string; why: string }> = {
    "strong-swe": {
      what: "Direct tech stack alignment — Python, FastAPI, Postgres, Kafka all appear in both documents.",
      why: "Demonstrates the engine's ability to identify and score exact technology matches across semantic, keyword, and bullet signals simultaneously.",
    },
    "strong-data": {
      what: "ML/recommendation systems specialist with production experience at brand-name companies.",
      why: "Tests the engine's field classification and title family matching when the resume uses different terminology than the JD.",
    },
    "partial-frontend": {
      what: "Strong React/frontend skills but the JD requires full-stack with backend production experience.",
      why: "Validates that the engine correctly identifies gaps (missing backend depth, no on-call experience) while still crediting the strong frontend overlap.",
    },
    "transfer-medical": {
      what: "MD/PhD clinical researcher applying to a health data scientist role — transferable statistics and research skills.",
      why: "Stress-tests the cross-domain field transfer scoring and seniority matching when career paths don't follow traditional software trajectories.",
    },
    "weak-pm-to-swe": {
      what: "Product manager applying for a senior backend engineering role — no coding experience.",
      why: "Proves the engine says no when it should. The title, field, and bullet signals all fire low, pulling the overall score down despite some keyword overlap.",
    },
    "strong-nursing": {
      what: "DNP nurse practitioner with 10 years of progressive clinical leadership applying to a nursing director role.",
      why: "Tests the engine on healthcare-specific language: certifications (FNP-BC, CCRN), clinical metrics (readmission rates, Press Ganey), and regulatory knowledge (Joint Commission, HIPAA).",
    },
    "partial-finance": {
      what: "Corporate FP&A analyst with strong modeling skills but no investment banking deal execution experience.",
      why: "Validates partial scoring — the candidate has real financial modeling skills (DCF, 3-statement) but the JD requires closed deal experience and LBO expertise the resume lacks.",
    },
    "strong-marketing": {
      what: "B2B SaaS growth marketer with proven funnel metrics (14K MQLs/month, $8M pipeline) applying to lead a team.",
      why: "Tests the engine on marketing-specific vocabulary: attribution modeling, lead scoring, HubSpot workflows, SEO, CAC/LTV. Different domain from tech roles but equally specific.",
    },
    "transfer-legal": {
      what: "Litigation attorney with FCPA investigation experience applying to a fintech compliance officer role.",
      why: "Tests cross-domain transfer within legal — courtroom litigation skills partially transfer to compliance (investigations, regulatory knowledge, policy drafting) but the candidate lacks direct SEC/FINRA compliance program experience.",
    },
    "partial-operations": {
      what: "Supply chain analyst with strong data skills and Lean Six Sigma but no people management experience.",
      why: "The JD requires managing 350 associates and 6 supervisors — a critical gap. The engine should credit the process improvement and analytical overlap while flagging the missing management track record.",
    },
  };

  const ex = explanations[sample.id];
  if (!ex) return null;

  return (
    <div className="rounded-lg bg-slate-900/40 border border-slate-800/40 p-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
        Why this sample
      </div>
      <div className="text-xs text-slate-300 leading-relaxed mb-1">
        {ex.what}
      </div>
      <div className="text-xs text-slate-500 leading-relaxed">
        {ex.why}
      </div>
    </div>
  );
}
